<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const togglingBotId = ref(null);

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

const toggleBotStatus = async inbox => {
  if (!inbox.agent_bot_info) return;

  togglingBotId.value = inbox.id;
  try {
    const result = await store.dispatch('inboxes/toggleAgentBotStatus', inbox.id);
    const statusMessage = result.active
      ? t('INBOX_MGMT.BOT_STATUS.ACTIVATED')
      : t('INBOX_MGMT.BOT_STATUS.DEACTIVATED');
    useAlert(statusMessage);
  } catch (error) {
    useAlert(t('INBOX_MGMT.BOT_STATUS.ERROR'));
  } finally {
    togglingBotId.value = null;
  }
};

const getBotStatusColor = inbox => {
  if (!inbox.agent_bot_info) return '';
  return inbox.agent_bot_info.active
    ? 'text-n-teal-11 bg-n-teal-3'
    : 'text-n-slate-11 bg-n-slate-3';
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.LIST.TABLE.INBOX') }}
            </th>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              Status
            </th>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.LIST.TABLE.BOT') }}
            </th>
            <th class="py-3 text-right text-sm font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.LIST.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <tr v-for="inbox in inboxesList" :key="inbox.id">
            <!-- Inbox Info -->
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center flex-row gap-4">
                <div
                  v-if="inbox.avatar_url"
                  class="bg-n-alpha-3 rounded-full size-12 p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <Avatar
                    :src="inbox.avatar_url"
                    :name="inbox.name"
                    :size="30"
                    rounded-full
                  />
                </div>
                <div
                  v-else
                  class="size-12 flex justify-center items-center bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <ChannelIcon class="size-5 text-n-slate-10" :inbox="inbox" />
                </div>
                <div>
                  <span class="block font-medium capitalize">
                    {{ inbox.name }}
                  </span>
                  <ChannelName
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                  />
                </div>
              </div>
            </td>

            <!-- Status Column -->
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div
                v-if="inbox.disabled_by_plan"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-ruby-50 dark:bg-ruby-900/20 text-ruby-700 dark:text-ruby-300 border border-ruby-200 dark:border-ruby-800"
              >
                <span class="i-lucide-ban size-3.5" />
                Desabilitada
              </div>
              <div
                v-else
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800"
              >
                <span class="i-lucide-check-circle size-3.5" />
                Ativa
              </div>
            </td>

            <!-- Bot Status Column -->
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div
                v-if="inbox.agent_bot_info"
                class="flex items-center gap-3"
              >
                <!-- Bot Badge -->
                <div
                  class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
                  :class="getBotStatusColor(inbox)"
                >
                  <span class="i-lucide-bot size-4" />
                  <span class="text-sm font-medium">
                    {{ inbox.agent_bot_info.name }}
                  </span>
                </div>

                <!-- Toggle Switch -->
                <div
                  v-if="isAdmin"
                  v-tooltip.top="
                    inbox.agent_bot_info.active
                      ? $t('INBOX_MGMT.BOT_STATUS.CLICK_TO_DEACTIVATE')
                      : $t('INBOX_MGMT.BOT_STATUS.CLICK_TO_ACTIVATE')
                  "
                  class="flex items-center"
                >
                  <Switch
                    :model-value="inbox.agent_bot_info.active"
                    :disabled="togglingBotId === inbox.id"
                    @update:model-value="toggleBotStatus(inbox)"
                  />
                </div>
              </div>
              <div v-else class="flex items-center gap-2 text-n-slate-10">
                <span class="i-lucide-bot-off size-4" />
                <span class="text-sm">{{ $t('INBOX_MGMT.BOT_STATUS.NO_BOT') }}</span>
              </div>
            </td>

            <!-- Actions -->
            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDelete(inbox)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
